import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, Chip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FakeButtonLink } from '../../../../components/FakeLink';
import { TableCard } from '../../../../components/Section';
import { Table } from '../../../../components/Table';
import { TableRef } from '../../../../components/Table/Table';
import { fetchStatuses } from '../../../../constants/fetchStatuses';
import { userPortfolioRequestStatusToInfo } from '../../../../constants/tables/userPortfolioRequest';
import { TREASURY_GRID_HEIGHT, TREASURY_LIST_PAGE_SIZE } from '../../../../constants/treasury';
import { fetchUserPortfolioRequests } from '../../../../redux/modules/userPortfolioRequest/userPortfolioRequest.actions';
import { userPortfolioRequestByRequestIdSelector } from '../../../../redux/modules/userPortfolioRequest/userPortfolioRequest.selectors';
import { ReducerState } from '../../../../redux/store.types';
import { locations } from '../../../../routes/locations';
import {
  UserPortfolioRequest,
  UserPortfolioRequestStatus,
  UserPortfolioRequestType,
} from '../../../../types/userPortfolioRequest';
import { round2Format } from '../../../../utils/number';

type TreasuryRedemptionRequestsListProps = {
  ownerUserId: number;
  subscribedPortfolioId: number;
  /** Bumped by the parent after a new redemption, so the list re-reads. */
  reloadKey: number;
};

const formatDateTime = ({ value }: { value?: string }) => (value ? moment(value).format('MM/DD/YYYY HH:mm') : '');

const COLUMNS: GridColDef<UserPortfolioRequest>[] = [
  {
    field: 'id',
    headerName: 'ID',
    width: 110,
    sortable: false,
    renderCell: ({ row }) => (
      <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.userPortfolioRequest(row.userPortfolio?.id)}>
        {row.id}
      </FakeButtonLink>
    ),
  },
  {
    field: 'changes.amount',
    headerName: 'Requested (SAR)',
    width: 150,
    sortable: false,
    valueGetter: ({ row }) => (row.changes?.redeemAll ? 'All' : round2Format(Number(row.changes?.amount) || 0)),
  },
  {
    field: 'status.name',
    headerName: 'Status',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      const status = row.status?.name as UserPortfolioRequestStatus;

      return (
        <Chip
          size="small"
          variant="outlined"
          color={userPortfolioRequestStatusToInfo[status]?.color ?? 'default'}
          label={status ?? '—'}
        />
      );
    },
  },
  {
    field: 'changes.reason',
    headerName: 'Reason',
    width: 240,
    sortable: false,
    valueGetter: ({ row }) => row.changes?.reason ?? '—',
  },
  { field: 'createdAt', headerName: 'Created', width: 170, sortable: false, valueFormatter: formatDateTime },
  { field: 'updatedAt', headerName: 'Updated', width: 170, sortable: false, valueFormatter: formatDateTime },
];

/**
 * The treasury portfolio's own redemption requests, from madkhol-portfolio (user_portfolio_request), filtered on the
 * server to this owner, this portfolio and REDEMPTION (madkhol-portfolio turns the dotted fields into nested where
 * clauses).
 */
export const TreasuryRedemptionRequestsList = ({
  ownerUserId,
  subscribedPortfolioId,
  reloadKey,
}: TreasuryRedemptionRequestsListProps) => {
  const requestId = `treasury-portfolio-${subscribedPortfolioId}-redemption-requests`;
  const tableRef = useRef<TableRef>(null);
  const { userPortfolioRequests, totalUserPortfolioRequests, fetchStatus } = useSelector((state: ReducerState) =>
    userPortfolioRequestByRequestIdSelector(state, requestId),
  );
  const loading = fetchStatus === fetchStatuses.pending;
  // Ids: the grid merges filters by id, and these three must all stay.
  const requiredFilters = useMemo(
    () => [
      { id: 1, field: 'userPortfolio.userId', operator: 'is', value: ownerUserId },
      { id: 2, field: 'userPortfolio.subscribedPortfolioId', operator: 'is', value: subscribedPortfolioId },
      { id: 3, field: 'type.name', operator: 'is', value: UserPortfolioRequestType.REDEMPTION },
    ],
    [ownerUserId, subscribedPortfolioId],
  );

  return (
    <TableCard
      title="Redemption Requests"
      description={`The portfolio's own redemption requests (madkhol-portfolio) on #${subscribedPortfolioId}.`}
      action={
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => tableRef.current?.reload()}>
          Refresh
        </Button>
      }
    >
      <Table
        key={`${requestId}-${reloadKey}`}
        ref={tableRef}
        fetchItems={fetchUserPortfolioRequests}
        fetchItemsCustomParams={{ requestId }}
        rows={loading ? [] : userPortfolioRequests}
        rowCount={loading ? 0 : totalUserPortfolioRequests}
        loading={loading}
        columns={COLUMNS}
        requiredFilters={requiredFilters}
        initialSortModel={[{ field: 'id', sort: 'desc' }]}
        initialPageSize={TREASURY_LIST_PAGE_SIZE}
        pageSizeOptions={[TREASURY_LIST_PAGE_SIZE]}
        disableColumnFilter
        wrapperHeight={TREASURY_GRID_HEIGHT}
      />
    </TableCard>
  );
};
